/**
 * ResolutionPatternsTab — Browse all 94 resolution patterns
 * Filter by framework, type, search. Expandable detail rows.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, titleCase } from '../../constants';

const KE = `${API_V1}/knowledge-engine`;

const th: React.CSSProperties = {
  textAlign: 'left' as const, padding: '8px 10px', fontSize: 11, fontWeight: 600,
  color: 'var(--text-tertiary)', borderBottom: '1px solid var(--border-subtle)',
  textTransform: 'uppercase' as const, letterSpacing: 0.5, cursor: 'pointer',
};
const td = (i: number): React.CSSProperties => ({
  padding: '6px 10px', fontSize: 12, color: 'var(--text-secondary)',
  borderBottom: '1px solid var(--border-subtle)',
  background: i % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.02)',
});
const filterBtn = (active: boolean): React.CSSProperties => ({
  padding: '4px 12px', borderRadius: 4, fontSize: 11, cursor: 'pointer', border: 'none',
  background: active ? 'var(--accent)' : 'var(--bg-elevated)',
  color: active ? '#fff' : 'var(--text-secondary)',
  transition: 'all 0.15s',
});
const badge = (bg: string, fg: string): React.CSSProperties => ({
  display: 'inline-block', padding: '1px 6px', borderRadius: 3, fontSize: 10,
  fontWeight: 600, background: bg, color: fg,
});

function fwBadge(fw: string) {
  if (fw === 'US_GAAP') return badge('#2563EB30', '#93C5FD');
  if (fw === 'IFRS') return badge('#16A34A30', '#4ADE80');
  return badge('#7C3AED30', '#A78BFA'); // BOTH
}
function typeBadge(t: string) {
  if (t === 'JOURNAL_ENTRY') return badge('#4ADE8020', '#4ADE80');
  return badge('#FACC1520', '#FACC15'); // FLAG_ONLY
}

interface Pattern {
  id: number;
  exception_type: string;
  pattern_name: string;
  framework: string;
  resolution_type: string;
  primary_standard?: string;
  times_used?: number;
  times_accepted?: number;
  times_modified?: number;
  times_rejected?: number;
  source?: string;
  je_template?: any;
  citation_text?: string;
  coso_reference?: string;
  root_cause_template?: string;
  recommendation_template?: string;
}

export default function ResolutionPatternsTab() {
  const [patterns, setPatterns] = useState<Pattern[]>([]);
  const [loading, setLoading] = useState(true);
  const [fwFilter, setFwFilter] = useState('ALL');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<number | null>(null);
  const [detail, setDetail] = useState<Pattern | null>(null);
  const [sortCol, setSortCol] = useState('exception_type');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const load = useCallback(() => {
    setLoading(true);
    authFetch(`${KE}/patterns`).then(r => r.ok ? r.json() : { patterns: [] }).then(d => {
      setPatterns(d?.patterns || []);
      setLoading(false);
    }).catch(() => { setPatterns([]); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  // Load detail on expand
  useEffect(() => {
    if (expanded === null) { setDetail(null); return; }
    authFetch(`${KE}/patterns/${expanded}`).then(r => r.ok ? r.json() : null).then(setDetail).catch(() => setDetail(null));
  }, [expanded]);

  // Filter
  let filtered = patterns;
  if (fwFilter !== 'ALL') filtered = filtered.filter(p => p.framework === fwFilter || p.framework === 'BOTH');
  if (typeFilter !== 'ALL') filtered = filtered.filter(p => p.resolution_type === typeFilter);
  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter(p =>
      p.exception_type.toLowerCase().includes(q) ||
      p.pattern_name.toLowerCase().includes(q) ||
      (p.primary_standard || '').toLowerCase().includes(q)
    );
  }

  // Sort
  const sorted = [...filtered].sort((a: any, b: any) => {
    const av = (a[sortCol] ?? '').toString().toLowerCase();
    const bv = (b[sortCol] ?? '').toString().toLowerCase();
    return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  const toggleSort = (col: string) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };
  const si = (col: string) => sortCol === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  if (loading) return <p style={{ color: 'var(--text-secondary)', padding: 20 }}>Loading resolution patterns...</p>;

  // Extract JE template debit/credit concepts
  const jeInfo = (p: Pattern) => {
    if (!p.je_template || !p.je_template.lines) return { dr: '--', cr: '--' };
    const lines = p.je_template.lines;
    const dr = lines.find((l: any) => l.direction === 'debit')?.xbrl_concept || '--';
    const cr = lines.find((l: any) => l.direction === 'credit')?.xbrl_concept || '--';
    return { dr, cr };
  };

  return (
    <div>
      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {['ALL', 'US_GAAP', 'IFRS', 'BOTH'].map(f => (
            <button key={f} onClick={() => setFwFilter(f)} style={filterBtn(fwFilter === f)}>
              {f === 'ALL' ? 'All FW' : f.replace('_', ' ')}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {['ALL', 'JOURNAL_ENTRY', 'FLAG_ONLY'].map(t => (
            <button key={t} onClick={() => setTypeFilter(t)} style={filterBtn(typeFilter === t)}>
              {t === 'ALL' ? 'All Types' : t === 'JOURNAL_ENTRY' ? 'JE' : 'Flag'}
            </button>
          ))}
        </div>
        <input
          type="text" placeholder="Search patterns..." value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            padding: '6px 12px', fontSize: 12, borderRadius: 6, flex: 1, minWidth: 180,
            border: '1px solid var(--border-subtle)', background: 'var(--bg-elevated)',
            color: 'var(--text-primary)', outline: 'none',
          }}
        />
        <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{sorted.length} patterns</span>
      </div>

      {/* Table */}
      <div style={{ maxHeight: 'calc(100vh - 260px)', overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)' }}>
          <thead>
            <tr>
              <th style={th} onClick={() => toggleSort('exception_type')}>Exception Type{si('exception_type')}</th>
              <th style={th} onClick={() => toggleSort('pattern_name')}>Pattern Name{si('pattern_name')}</th>
              <th style={th} onClick={() => toggleSort('framework')}>FW{si('framework')}</th>
              <th style={th} onClick={() => toggleSort('resolution_type')}>Type{si('resolution_type')}</th>
              <th style={th} onClick={() => toggleSort('primary_standard')}>Standard{si('primary_standard')}</th>
              <th style={th}>DR Concept</th>
              <th style={th}>CR Concept</th>
              <th style={th} onClick={() => toggleSort('times_used')}>Used{si('times_used')}</th>
              <th style={th} onClick={() => toggleSort('times_accepted')}>Acc{si('times_accepted')}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((p, i) => {
              const { dr, cr } = jeInfo(p);
              const isExpanded = expanded === p.id;
              return (
                <React.Fragment key={p.id}>
                  <tr onClick={() => setExpanded(isExpanded ? null : p.id)} style={{ cursor: 'pointer' }}>
                    <td style={{ ...td(i), fontWeight: 600 }}>{titleCase(p.exception_type)}</td>
                    <td style={td(i)}>{p.pattern_name}</td>
                    <td style={td(i)}><span style={fwBadge(p.framework)}>{p.framework.replace('_', ' ')}</span></td>
                    <td style={td(i)}><span style={typeBadge(p.resolution_type)}>{p.resolution_type === 'JOURNAL_ENTRY' ? 'JE' : 'FLAG'}</span></td>
                    <td style={{ ...td(i), fontVariantNumeric: 'tabular-nums', fontSize: 11 }}>{p.primary_standard || '--'}</td>
                    <td style={{ ...td(i), fontSize: 10, fontVariantNumeric: 'tabular-nums', color: '#60A5FA' }}>{dr}</td>
                    <td style={{ ...td(i), fontSize: 10, fontVariantNumeric: 'tabular-nums', color: '#FB923C' }}>{cr}</td>
                    <td style={{ ...td(i), fontVariantNumeric: 'tabular-nums', textAlign: 'center' }}>{p.times_used ?? 0}</td>
                    <td style={{ ...td(i), fontVariantNumeric: 'tabular-nums', textAlign: 'center' }}>{p.times_accepted ?? 0}</td>
                  </tr>
                  {isExpanded && detail && (
                    <tr>
                      <td colSpan={9} style={{ padding: '16px 20px', background: 'rgba(74,222,128,0.06)', borderBottom: '2px solid var(--border-subtle)' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                          {/* JE Template */}
                          {detail.je_template?.lines && (
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', marginBottom: 6, textTransform: 'uppercase' }}>JE Template</div>
                              {detail.je_template.lines.map((l: any, li: number) => (
                                <div key={li} style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', marginBottom: 3 }}>
                                  <span style={{ color: l.direction === 'debit' ? '#60A5FA' : '#FB923C', fontWeight: 600, width: 30, display: 'inline-block' }}>
                                    {l.direction === 'debit' ? 'DR' : 'CR'}
                                  </span>
                                  <span style={{ color: 'var(--text-primary)' }}>{l.xbrl_concept || 'N/A'}</span>
                                  <span style={{ color: 'var(--text-tertiary)', marginLeft: 8 }}>({l.account_type})</span>
                                  <span style={{ color: 'var(--text-secondary)', marginLeft: 8 }}>${l.amount_formula}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {/* Quality + Citations */}
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', marginBottom: 6, textTransform: 'uppercase' }}>Quality</div>
                            <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                              Used: {detail.times_used ?? 0} ·
                              Accepted: {detail.times_accepted ?? 0} ·
                              Modified: {detail.times_modified ?? 0} ·
                              Rejected: {detail.times_rejected ?? 0}
                              {(detail.times_used ?? 0) > 0 && (
                                <span style={{ color: '#4ADE80', marginLeft: 8 }}>
                                  ({Math.round(((detail.times_accepted ?? 0) / (detail.times_used ?? 1)) * 100)}% accepted)
                                </span>
                              )}
                              <br />Source: {detail.source || 'PLATFORM'}
                            </div>
                            {detail.citation_text && (
                              <>
                                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', marginTop: 10, marginBottom: 4, textTransform: 'uppercase' }}>Citation</div>
                                <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontStyle: 'italic', lineHeight: 1.5, maxHeight: 60, overflow: 'hidden' }}>
                                  "{detail.citation_text}"
                                </div>
                              </>
                            )}
                            {detail.coso_reference && (
                              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 6 }}>COSO: {detail.coso_reference}</div>
                            )}
                          </div>
                        </div>
                        {/* Root cause + recommendation */}
                        {(detail.root_cause_template || detail.recommendation_template) && (
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 12 }}>
                            {detail.root_cause_template && (
                              <div>
                                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', marginBottom: 4, textTransform: 'uppercase' }}>Root Cause</div>
                                <div style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{detail.root_cause_template}</div>
                              </div>
                            )}
                            {detail.recommendation_template && (
                              <div>
                                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', marginBottom: 4, textTransform: 'uppercase' }}>Recommendation</div>
                                <div style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{detail.recommendation_template}</div>
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {sorted.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 12 }}>
            No patterns match the current filters.
          </div>
        )}
      </div>
    </div>
  );
}
